import { useState } from "react";
import { PRODUCTS } from "@/content/products";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { ExplorerFrame, Swap } from "@/components/site/ExplorerFrame";
import { Container, Eyebrow, Mono, SectionTitle, StatusMarker } from "@/components/site/primitives";
import { ProductFoundation, ProductTop } from "@/components/products/ProductParts";
import { cn } from "@/lib/utils";

const P = PRODUCTS.lumen;

const Hero = () => (
  <section data-testid="lumen-hero">
    <Container className="grid gap-10 py-14 lg:grid-cols-12 lg:items-end lg:py-20">
      <Placeholder label={P.placeholder} note="Landscape, the human setting of education" ratio="16 / 10" className="fade-up lg:col-span-7" testid="placeholder-lumen-hero" />
      <div className="fade-up delay-1 lg:col-span-5">
        <Eyebrow index={P.index}>Product · {P.activity}</Eyebrow>
        <h1 data-testid="page-title" className="mt-6 font-display text-5xl font-extrabold tracking-[-0.02em] sm:text-6xl">Lumen</h1>
        <p className="mt-6 text-o-dim sm:text-lg">{P.tagline}</p>
        <div className="mt-8"><StatusMarker testid="status-lumen" /></div>
      </div>
    </Container>
  </section>
);

const Nest = ({ i, sel, onSelect }) => {
  const x = P.scales[i];
  const on = sel === x.id;
  const last = i === P.scales.length - 1;
  return (
    <div className={cn("border p-3 transition-colors sm:p-4", on ? "border-o-accent bg-o-soft" : "border-o-strong", last && "min-h-[96px]")}>
      <button type="button" onClick={() => onSelect(x.id)} aria-pressed={on} data-testid={`lumen-scale-${x.id}`} className="flex items-baseline gap-2 text-left">
        <Mono accent={on}>{x.index}</Mono>
        <span className="font-display text-sm font-bold sm:text-base">{x.label}</span>
      </button>
      {!last && <div className="mt-3 sm:mt-4"><Nest i={i + 1} sel={sel} onSelect={onSelect} /></div>}
    </div>
  );
};

const Scales = () => {
  const [sel, setSel] = useState("student");
  const [mode, setMode] = useState("explore");
  const s = P.scales.find((x) => x.id === sel);
  return (
    <ExplorerFrame testid="lumen-scales" label="Scales of learning" hint="From one student to the continuity of learning over time. Select a scale." mode={mode} onMode={setMode}>
      {mode === "explore" ? (
        <div className="grid lg:grid-cols-12">
          <div className="border-b border-o-line p-5 sm:p-7 lg:col-span-7 lg:border-b-0 lg:border-r">
            <Nest i={0} sel={sel} onSelect={setSel} />
          </div>
          <div className="p-5 sm:p-7 lg:col-span-5" aria-live="polite">
            <Swap k={sel}>
              <Mono accent>Scale {s.index}</Mono>
              <h3 className="mt-3 font-display text-2xl font-bold" data-testid="lumen-scale-title">{s.label}</h3>
              <p className="mt-3 leading-relaxed">{s.text}</p>
              <Placeholder label={`Lumen · ${s.label.toLowerCase()}`} ratio="4 / 3" className="mt-6" />
            </Swap>
          </div>
        </div>
      ) : (
        <dl className="divide-y divide-o-line" data-testid="lumen-scales-text">
          {[...P.scales].reverse().map((x) => (
            <div key={x.id} className="grid gap-2 p-5 sm:grid-cols-12 sm:p-7">
              <dt className="font-display text-lg font-bold sm:col-span-4">{x.label}</dt>
              <dd className="text-o-dim sm:col-span-8">{x.text}</dd>
            </div>
          ))}
        </dl>
      )}
    </ExplorerFrame>
  );
};

const Settings = () => (
  <section className="border-t border-o-line" data-testid="lumen-settings">
    <Container className="grid gap-4 py-16 md:grid-cols-2 lg:py-24">
      <div>
        <Placeholder label="Lumen · student development imagery" ratio="4 / 3" />
        <p className="mt-4 text-sm text-o-dim">Space for an explanation of the student's experience, supplied in Phase 3.</p>
      </div>
      <div className="md:mt-16">
        <Placeholder label="Lumen · classroom participation imagery" ratio="4 / 3" />
        <p className="mt-4 text-sm text-o-dim">Space for an explanation of the classroom experience, supplied in Phase 3.</p>
      </div>
    </Container>
  </section>
);

export default function LumenPage() {
  return (
    <>
      <ProductTop id="lumen" />
      <Hero />
      <section className="border-t border-o-line" data-testid="lumen-scales-section">
        <Container className="py-16 lg:py-24">
          <div className="mb-10 max-w-3xl">
            <Eyebrow index="01">Student · Classroom · Collective · Continuity</Eyebrow>
            <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">Learning held at every scale it actually happens.</SectionTitle>
          </div>
          <Scales />
        </Container>
      </section>
      <Settings />
      <ProductFoundation product={P} />
      <EngageBand path="research" from="/products/lumen" title="Discuss Lumen."
        text="Product, research, and institutional inquiries. During Phase 1 this inquiry is an inactive preview." />
    </>
  );
}
